'use client'

import React, { useEffect } from 'react'
import { EventHandler } from './event-handler'
import { EventTrackerQueue } from './event-tracker-queue'
import { EVENT_TYPE } from './event-type'
import { TrackEventCommandFactory } from './track-event-command-factory'
import { Event } from './event'
import { EventPayloadSnakeCaseTransformer } from './event-handlers/event-payload-snake-case-transformer'
import { EventTracker } from './event-tracker'
import { EventNameAppender } from './event-handlers/event-name-appender'
import { EventNameBuilder } from './event-handlers/event-name-builder'
import { EventNamePageMapper } from './event-handlers/event-name-page-mapper'
import { EventTrackerContextCleaner } from './event-handlers/event-tracker-context-cleaner'
import { EventSpaceKeyAppender } from './event-handlers/event-space-key-appender'
import { createScrolledEvent } from './event-creators'
import logUtil from '../../../utils/log-util'
import { useLocation } from 'react-router-dom'

const eventTracker = new EventTracker(() => null)

const trackEventCommandFactory = new TrackEventCommandFactory()
trackEventCommandFactory.register(EVENT_TYPE.PRESSED, (tracker, event) =>
  tracker.recordClick(event),
)
trackEventCommandFactory.register(EVENT_TYPE.SHOWN, (tracker, event) =>
  tracker.recordImpression(event),
)
trackEventCommandFactory.register(EVENT_TYPE.SCROLLED, (tracker, event) =>
  tracker.recordImpression(event),
)

const eventTrackerQueue = new EventTrackerQueue(
  eventTracker,
  trackEventCommandFactory,
)

const eventNamePageMapper = new EventNamePageMapper()
eventNamePageMapper.addMapping('/dashboard', 'home')
eventNamePageMapper.addMapping(
  /\/dashboard\/applications\/\w+/,
  'app-credentials',
)
eventNamePageMapper.addMapping(
  '/dashboard/upgrade-your-apps',
  'upgrade-your-apps',
)
eventNamePageMapper.addMapping(
  '/dashboard/upgrade-your-apps/upgrade-guide',
  'upgrade-guide',
)

const eventNameBuilder = new EventNameBuilder()
eventNameBuilder.withPrefix('devexp_devdash')

const eventHandler = new EventHandler()
eventHandler
  .setNext(new EventNameAppender(eventNamePageMapper, eventNameBuilder))
  .setNext(new EventSpaceKeyAppender('SKEXJE'))
  .setNext(new EventPayloadSnakeCaseTransformer())
  .setNext(eventTrackerQueue)
  .setNext(new EventTrackerContextCleaner())

function trackEvent(event) {
  eventHandler.handle(event)
}

const firedThresholds = new Set()
const SCROLL_THRESHOLDS = [0.25, 0.5, 0.75, 1.0]
function handleScrollEvent() {
  const scrollPosition = window.scrollY + window.innerHeight
  const documentHeight = document.documentElement.scrollHeight

  SCROLL_THRESHOLDS.forEach((threshold) => {
    if (
      scrollPosition >= documentHeight * threshold &&
      !firedThresholds.has(threshold)
    ) {
      trackEvent(createScrolledEvent({ scrollDepth: threshold }))
      firedThresholds.add(threshold)
    }
  })
}

export const TrackEventContext = React.createContext(null)
export function TrackEventProvider({
  children,
  trackScrolled = false,
  track = trackEvent,
  trackQueue = eventTrackerQueue,
}) {
  useEffect(function processEvents() {
    const interval = setInterval(() => {
      try {
        trackQueue.process()
      } catch (error) {
        logUtil.error(error)
      }
    }, 1000)

    return function () {
      clearInterval(interval)
    }
  }, [])

  const location = useLocation()
  useEffect(
    function trackScrolledEvent() {
      if (trackScrolled) {
        window.addEventListener('scroll', handleScrollEvent)
      }

      return () => {
        if (trackScrolled) {
          firedThresholds.clear()
          window.removeEventListener('scroll', handleScrollEvent)
        }
      }
    },
    [location],
  )

  return (
    <TrackEventContext.Provider value={{ track }}>
      {children}
    </TrackEventContext.Provider>
  )
}

export function useTrackEvent() {
  const trackEventContext = React.useContext(TrackEventContext)
  const { track } = trackEventContext

  /**
   * Tracks an event with the specified type, name, and payload.
   *
   * @param {Event} event - the event to track
   * @returns {void}
   */
  function _trackEvent(event) {
    const type = event.getType()
    const { name, ...payload } = event.getPayload()
    track(new Event(type, { ...payload, eventComponentName: name }))
  }

  return _trackEvent
}
